import { SportOfferDataStore } from '@store/modules/data/sportOffer';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import { CompetitionDataStore } from '../store/data';
import {
    computed,
    Ref,
    ref,
} from '@vue/composition-api';
import {
    tournamentName as getTournamentName,
    categoryName as getCategoryName,
    sportName as getSportName,
    categoryUrlName,
} from '@modules/eventGrid/nameExtractors';
import { SportOfferUIStore } from '@store/modules/ui/sportOffer/sports/sportsIndex';
import { CompetitionsUIStore } from '../store/ui';
import { slugify } from '@app/utils/slugify';
import { deslugify } from '@app/utils/deslugify';
import { useCompetitionRouteParams } from './useCompetitionRouteParams';
import { errorGuard } from '@core/utils/ErrorGuard';
import { simplifyTournamentName } from '@core/utils/sportsOffer/simplifyTournamentName';
import { NavigationDataStore } from '@store/modules/navigation';
import { appLocale } from '@src/config/locale';
import { DateTimeFormatOptions } from 'vue-i18n';
import { processContentTitle } from '@src/app/seo/helpers';
import { featureFlagService } from '@superbet-group/web.lib.flags';

export function useCompetitionsDataSources() {
    const isDataLoaded = ref(false);

    const sportOfferStore = useVuexStore<SportOfferDataStore>('data/sportOffer');
    const competitionsStore = useVuexStore<CompetitionDataStore>('data/competitions');
    const sportsUiStore = useVuexStore<SportOfferUIStore>('ui/sportOffer/sports');
    const competitionUiStore = useVuexStore<CompetitionsUIStore>('ui/competitions');
    const navigationModule = useVuexStore<NavigationDataStore>('navigation');

    const { tournamentSlug } = useCompetitionRouteParams();

    const tournaments = sportOfferStore.getter('tournaments');
    const isOfferFetched = sportOfferStore.getter('isOfferFetched');
    const getIsOfferFetchedBySport = sportOfferStore.getter('getIsOfferFetchedBySport');
    const availableTournamentIds = competitionsStore.getter('availableTournamentIds');
    const currentCompetition = competitionsStore.getter('currentVirtualTournament');
    const competitionEvents = sportsUiStore.getter('competitionEvents');
    const outrightOffer = competitionsStore.getter('outrightOffer');
    const isLoadingCompetition = competitionsStore.getter('isLoadingCompetition');
    const currentSportId = competitionUiStore.getter('currentSportId');
    const currentCategoryId = competitionUiStore.getter('currentCategoryId');
    const currentTournamentId = competitionUiStore.getter('currentTournamentId');
    const currentSeasonId = competitionUiStore.getter('currentSeasonId');
    const competitionId = competitionUiStore.getter('competitionId');
    const selectedTab = competitionUiStore.state('selectedTab');
    const fetchEventsForTournaments = sportOfferStore.action('fetchEventsForTournaments');
    const fetchOutrights = sportOfferStore.action('fetchOutrights');
    const fetchCompetitionById = competitionsStore.action('fetchCompetitionById');
    const fetchCompetitionDetailsByTournamentId = competitionsStore.action('fetchCompetitionDetailsByTournamentId');
    const fetchCompetitionDetailsFromOfferMappings = competitionsStore.action('fetchCompetitionDetailsFromOfferMappings');
    const clearCurrentCompetition = competitionsStore.action('clearCurrentCompetition');

    const competitionName = computed(() => {
        const tournamentId = availableTournamentIds.value[0];

        if (currentCompetition.value) {
            return currentCompetition.value.name;
        } else if (tournamentId) {
            return getTournamentName(tournamentId);
        } else if (isLoadingCompetition.value) {
            return '...';
        } else {
            const categoryName = currentCategoryId.value ? categoryUrlName(currentCategoryId.value) : '';
            const matchingTournament = Object.values(tournaments.value)
                .find((tournament) => {
                    const tournamentName = simplifyTournamentName(tournament.urlName, categoryName);
                    return slugify(tournamentName) === tournamentSlug.value;
                });

            return matchingTournament
                ? simplifyTournamentName(matchingTournament.urlName, categoryName)
                : deslugify(tournamentSlug.value);
        }
    });

    const seoCompetitionTitle = computed(() => {
        const contentTitle = navigationModule.getter('routeSeoData').value?.title;
        if (contentTitle) {
            return processContentTitle(contentTitle, competitionName.value);
        } else {
            return competitionName.value;
        }
    });

    const categoryName = computed(() => currentCategoryId.value ? getCategoryName(currentCategoryId.value) : '');
    const sportName = computed(() => currentSportId.value ? getSportName(currentSportId.value) : '');
    const isRelevantOfferFetched = computed((): boolean => {
        return currentSportId.value
            ? getIsOfferFetchedBySport.value(currentSportId.value)
            : isOfferFetched.value;
    });

    function fetchInitialData() {
        fetchOutrights();
    }

    function clearCompetitionState() {
        clearCurrentCompetition();
    }

    async function initialiseDataSources(payload: FetchCompetitionOrTournament): Promise<void> {
        isDataLoaded.value = false;

        await resolveCompetitionOrTournament(payload);

        const competition = currentCompetition.value;

        if (payload.requestActiveOffer && competition) {
            await errorGuard(() => fetchEventsForTournaments(competition.childrenIds));
        }

        isDataLoaded.value = true;
    }

    async function resolveCompetitionOrTournament(payload: FetchCompetitionOrTournament): Promise<void> {
        if (payload.tournamentId && !Number.isNaN(payload.tournamentId)) {
            // We know what tournament we are dealing with already, we just need to fetch the full competition
            await errorGuard(() => fetchCompetitionDetailsByTournamentId(payload.tournamentId!));
        } else if (payload.competitionId && currentCompetition.value?.competitionId !== payload.competitionId) {
            // Fall back to using a competition for this
            await fetchCompetitionById({ competitionId: payload.competitionId!, sportId: payload.sportId });
            isDataLoaded.value = true;
        } else if (payload.competitionId && currentCompetition.value?.competitionId === payload.competitionId) {
            isDataLoaded.value = true;
        } else {
            const useOfferMappingsToResolveCompetitions = await featureFlagService.getFeatureValue('web.sportOffer.useOfferMappingsToResolveCompetitions', false);
            if (useOfferMappingsToResolveCompetitions) {
                await fetchCompetitionDetailsFromOfferMappings({
                    sportSlug: payload.sportSlug,
                    categorySlug: payload.categorySlug,
                    tournamentSlug: payload.tournamentSlug,
                });
            }
        }
    }

    const competitionEventsDateRange: Ref<string | null> = computed(() => {
        if (!competitionEvents.value.length) {
            return null;
        }

        const dates = competitionEvents.value.map((event) => event.date);
        const minDate = new Date(Math.min(...dates.map((date) => date.getTime())));
        const maxDate = new Date(Math.max(...dates.map((date) => date.getTime())));

        const formatConfig: DateTimeFormatOptions = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        };

        return `${minDate.toLocaleDateString(appLocale.locale, formatConfig)} - ${maxDate.toLocaleDateString(appLocale.locale, formatConfig)}`;
    });

    return {
        isOfferFetched: computed(() => isRelevantOfferFetched.value || isDataLoaded.value),
        isDataLoaded,
        isLoadingCompetition,
        selectedTab,
        sportName,
        categoryName,
        competitionName,
        seoCompetitionTitle,
        competitionEvents,
        outrightOffer,
        currentSportId,
        competitionId,
        currentSeasonId,
        currentCategoryId,
        currentTournamentId,
        competitionEventsDateRange,
        initialiseDataSources,
        fetchInitialData,
        clearCompetitionState,
    };
}

type FetchCompetitionOrTournament = {
    tournamentId?: number;
    competitionId?: number;
    sportId: number;
    requestActiveOffer: boolean;
    sportSlug?: string;
    categorySlug?: string;
    tournamentSlug?: string;
};
